import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { clearFix } from 'polished';

import breakpoints from '../styles/breakpoints';
import { formatPrice } from '../utils/numbers';

import CartItem from './CartItem';
import { PrimaryLinkButton } from './Buttons';
import { dataLayer } from '../utils/window';

const EmptyCartMessage = styled.div`
  text-align: center;
`;

const StyledCart = styled.div`
  margin: 0 auto 160px auto;
  ${clearFix()};
`;

const Title = styled.h2``;

const ProductList = styled.div`
  margin-bottom: 60px;
  display: block;
`;

const Summary = styled.div`
  width: 100%;

  ${breakpoints.large`
        max-width: 390px;
        float: right;
    `};
`;

const SummaryItem = styled.div`
  display: flex;
  font-size: 1.125rem;
  justify-content: space-between;
  padding: 14px 0;
`;

const Price = styled(SummaryItem)``;

const Shipping = styled(SummaryItem)`
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const ShippingDiscount = styled(SummaryItem)`
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const Total = styled(SummaryItem)`
  font-weight: 700;
  border-top: 1px solid ${p => p.theme.colors.primary};
`;

const CheckoutButton = styled(PrimaryLinkButton)`
  float: right;
  margin-top: 20px;
`;

export default function Cart({ cart, onRemoveProductFromCart = () => {} }) {
  const { products, sumProducts, sumShipping, shippingDiscount } = cart;

  if (!products || products.length === 0) {
    return (
      <EmptyCartMessage>
        <h2>Handlekurven din er tom!</h2>
        <PrimaryLinkButton to="/bildeler/">Finn bildeler</PrimaryLinkButton>
      </EmptyCartMessage>
    );
  }

  const cartProductsList = products.map(product => (
    <CartItem
      key={product.productIndx}
      product={{
        id: product.part.id,
        price: product.price,
        name: product.name
      }}
      handleRemoveFromCart={() =>
        onRemoveProductFromCart(product.productIndx, {
          ...product.part,
          price: product.price
        })
      }
    />
  ));

  let total = sumProducts + sumShipping - shippingDiscount;

  return (
    <StyledCart>
      <Title>Din Handlekurv</Title>

      <ProductList>{cartProductsList}</ProductList>

      <Summary>
        <Price>
          <span>Pris:</span> {formatPrice(sumProducts)}
        </Price>

        <Shipping>
          <span>Frakt:</span> {formatPrice(sumShipping)}
        </Shipping>

        {(shippingDiscount && (
          <ShippingDiscount>
            <span>Fraktrabatt:</span> – {formatPrice(shippingDiscount)}
          </ShippingDiscount>
        )) ||
          ''}

        <Total>
          <span>Totalt:</span> {formatPrice(total)}
        </Total>

        <CheckoutButton
          to="/checkout/"
          onClick={() => {
            dataLayer.push({
              event: 'checkout',
              ecommerce: {
                checkout: {
                  actionField: { step: 1, option: 'klarna' },
                  products: products.map(product => {
                    return {
                      price: product.price,
                      ...product.part
                    };
                  })
                }
              }
            });
          }}
        >
          Gå til kassen
        </CheckoutButton>
      </Summary>
    </StyledCart>
  );
}

Cart.propTypes = {
  cart: PropTypes.object.isRequired,
  onRemoveProductFromCart: PropTypes.func
};
